import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { Modal } from 'vet-bones/design-system/components';

import {
  VERIFY_EMAIL_PAGE,
  VERIFY_EMAIL_PAGE_BACK_TO_LOGIN,
  VERIFY_EMAIL_PAGE_CONTACT_US,
  VERIFY_EMAIL_PAGE_RESEND_BUTTON,
  VERIFY_EMAIL_PAGE_RESEND_MESSAGE,
} from 'src/ui/constants/testIds';
import { useProfile } from 'src/ui/hooks/connection';
import { components } from 'src/ui/templates/copy';
import { redirectToUrl } from 'src/ui/utils/window';

const { verifyEmailPage: copy } = components;

interface EmailStatus {
  email: string;
}

export const VerifyEmailPage: React.VFC = () => {
  const { data: profileData } = useProfile();

  React.useEffect(() => {
    if (profileData?.profile?.is_authenticated) {
      redirectToUrl('/');
    }
  }, [profileData]);

  const status = useQuery<EmailStatus>(
    'email-verification-status',
    async () => {
      const res = await fetch('/oauth/verify-email/status');
      if (!res.ok) throw new Error('Invalid session');
      return res.json();
    },
    {
      retry: false,
      onError: () => redirectToUrl('/login'),
    }
  );

  const resend = useMutation(async () => {
    const res = await fetch('/oauth/verify-email/resend', {
      method: 'POST',
    }).catch(() => {
      throw new Error(copy.resendError);
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || copy.resendError);
    }
    return data;
  });

  const handleResend = (e: React.MouseEvent) => {
    e.preventDefault();
    resend.reset();
    resend.mutate();
  };

  if (!status.data?.email || profileData?.profile?.is_authenticated) {
    return null;
  }

  return (
    <div className="sd-verify-email" data-testid={VERIFY_EMAIL_PAGE}>
      <Modal
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        className="sd-verify-email__modal"
        headerSize="md"
        isCloseButtonShown={false}
        isOpen
        title={copy.title}
        usePortal={false}
      >
        {copy.description(status.data.email)}
        <div className="sd-verify-email__actions">
          <p>
            {copy.resendPrompt}{' '}
            <a
              data-testid={VERIFY_EMAIL_PAGE_RESEND_BUTTON}
              href="#"
              onClick={handleResend}
            >
              {copy.resendLink}
            </a>
          </p>
          {resend.isSuccess ? (
            <p
              className="sd-verify-email__resend-message sd-verify-email__resend-message--success"
              data-testid={VERIFY_EMAIL_PAGE_RESEND_MESSAGE}
            >
              {copy.resendSuccess}
            </p>
          ) : null}
          {resend.isError ? (
            <p
              className="sd-verify-email__resend-message sd-verify-email__resend-message--error"
              data-testid={VERIFY_EMAIL_PAGE_RESEND_MESSAGE}
            >
              {resend.error instanceof Error
                ? resend.error.message
                : copy.resendError}
            </p>
          ) : null}
          <p className="sd-verify-email__secondary">
            {copy.contactPrompt}{' '}
            <a
              data-testid={VERIFY_EMAIL_PAGE_CONTACT_US}
              href={copy.contactUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              {copy.contactLink}
            </a>
          </p>
          <a data-testid={VERIFY_EMAIL_PAGE_BACK_TO_LOGIN} href="/login">
            {copy.backToLogin}
          </a>
        </div>
      </Modal>
    </div>
  );
};
